// Trash bin (v122). Lists soft-deleted artifacts + repos with
// Restore and Purge-now actions. Items are auto-purged 30 days
// after deletion by the reaper goroutine; the "expires in" badge
// shows the countdown.

import { Trash2, RotateCcw, Box, Package, AlertTriangle, FolderOpen } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useTrash, restoreTrash, purgeTrash, type TrashItem } from '../api/trash';
import { useWhoami } from '../api/client';

function daysLeft(expiresAt: string): number {
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function expiryTone(days: number): 'danger' | 'warning' | 'neutral' {
  if (days <= 3) return 'danger';
  if (days <= 7) return 'warning';
  return 'neutral';
}

export function Trash() {
  const { data, mutate } = useTrash();
  const { user } = useWhoami();
  // Permanent deletion before the 30-day reaper is org-admin only (backend
  // enforces it too). A project owner can restore but not purge early.
  const canPurge = !!(user?.roles?.includes('org_admin') || user?.roles?.includes('admin'));

  const onRestore = async (item: TrashItem) => {
    try {
      await restoreTrash(item.kind, item.id);
    } catch (e) {
      alert(`Restore failed: ${e}`);
    }
    await mutate();
  };

  const onPurge = async (item: TrashItem) => {
    const label =
      item.kind === 'repo'
        ? 'repository'
        : item.kind === 'project'
          ? 'project (and all its repositories)'
          : 'artifact';
    if (
      !confirm(
        `Permanently delete ${label} "${item.title}"? This cannot be undone — the underlying storage is removed if no other artifact references it.`,
      )
    ) {
      return;
    }
    try {
      await purgeTrash(item.kind, item.id);
    } catch (e) {
      alert(`Purge failed: ${e}`);
    }
    await mutate();
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-warning/80 mb-2">Recycle bin</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Trash</h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">
          Deleted artifacts and repositories stay here for{' '}
          <span className="text-fg font-medium">30 days</span>. After that the
          background reaper purges them and removes the underlying storage.
          Restore brings the item back into the live listing; Purge now
          deletes immediately.
        </p>
      </div>

      <Card>
        <CardHeader
          title={`Trashed items (${data?.length ?? 0})`}
          description="Auto-refreshed every 30 seconds. Items are sorted newest deletion first."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Kind</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Item</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Deleted by</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Deleted</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Expires</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {data?.length ? (
                data.map((item) => {
                  const days = daysLeft(item.expires_at);
                  return (
                    <tr key={`${item.kind}-${item.id}`} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                      <td className="px-5 py-3">
                        {item.kind === 'repo' ? (
                          <Badge tone="nebula">
                            <Box className="h-3 w-3" />
                            Repo
                          </Badge>
                        ) : item.kind === 'project' ? (
                          <Badge tone="warning">
                            <FolderOpen className="h-3 w-3" />
                            Project
                          </Badge>
                        ) : (
                          <Badge tone="accent">
                            <Package className="h-3 w-3" />
                            Artifact
                          </Badge>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-fg font-mono text-xs break-all max-w-md">{item.title}</p>
                        <p className="text-fg-muted text-xs">{item.subtitle}</p>
                      </td>
                      <td className="px-5 py-3 text-fg-muted text-xs">{item.deleted_by || '—'}</td>
                      <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                        {new Date(item.deleted_at).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-xs whitespace-nowrap">
                        <Badge tone={expiryTone(days)}>
                          {days === 0 ? 'today' : `in ${days}d`}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => onRestore(item)}
                            title="Restore"
                            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-success/10 hover:text-success"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                          {canPurge && (
                            <button
                              type="button"
                              onClick={() => onPurge(item)}
                              title="Permanently delete now (org_admin)"
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                    Trash is empty. Deleted items show up here for 30 days.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
